'use client';

import React, { useEffect } from 'react';
import Link from 'next/link';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';

interface ErrorPageProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function Error({ error, reset }: ErrorPageProps) {
  useEffect(() => {
    console.error('Application error:', error);
  }, [error]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4 py-20">
      <div className="max-w-xl w-full text-center">
        {/* Visual */}
        <div className="relative inline-flex items-center justify-center mb-8">
          <span className="text-[10rem] font-extrabold leading-none text-destructive/10 select-none">500</span>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-destructive/10 rounded-2xl p-5">
              <AlertTriangle size={52} className="text-destructive" />
            </div>
          </div>
        </div>

        <h1 className="text-3xl font-bold text-foreground mb-3">Something Went Wrong</h1>
        <p className="text-muted-foreground text-lg mb-2">
          Our servers hit an unexpected snag.
        </p>
        <p className="text-muted-foreground mb-10">
          Don&apos;t worry — our team has been notified. Please try again in a moment.
        </p>

        {error?.digest && (
          <p className="text-xs text-muted-foreground/60 mb-8 font-mono">
            Error ID: {error.digest}
          </p>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={reset}
            className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-xl font-medium hover:bg-primary/90 transition-colors"
          >
            <RefreshCw size={16} />
            Try Again
          </button>
          <Link
            href="/home"
            className="inline-flex items-center justify-center gap-2 border border-border bg-background text-foreground px-6 py-3 rounded-xl font-medium hover:bg-accent transition-colors"
          >
            <Home size={16} />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
